import { useContext, useState } from 'react';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { AssetContext } from '@pages/Asset/Asset.query';

import { Text } from '@components';

import { getProductValue } from '@utils/getProductValue';
import {
  Assignee,
  DataSection,
  EditableCustomField,
  EditableDateField,
  EditableField,
} from './components';
import { ProductSpecsBody } from './components/ProductSpec';
import { UnEditableField } from './components/UnEditableField/UnEditableField';
import { Chevron, Head, Item, ItemBody } from './Data.styles';
import { LabelMenu } from './LabelMenu';
import { DataItem } from './DataItem';
import { Items } from './utils';

export const InternalData = () => {
  const { equipmentQuery, customFieldQuery, configQuery } =
    useContext(AssetContext);

  const config = configQuery?.data;

  const [openInternalData, setOpenInternalData] = useState(false);

  const customFields = customFieldQuery?.data;

  const { productId } = getProductValue(equipmentQuery?.data);

  const equipment = equipmentQuery?.data;
  if (
    !equipment ||
    !customFieldQuery ||
    customFieldQuery.isLoading ||
    customFieldQuery.isError ||
    !customFields ||
    !config
  ) {
    return <div />;
  }

  const fieldsContent = equipment.fields;

  return (
    <div>
      <Item>
        <Head
          data-testid="inventory-equipment-internal-data"
          onClick={() => {
            setOpenInternalData(!openInternalData);
          }}
        >
          <Text text="h400" textColor="n800">
            Internal Data
          </Text>
          <Chevron>
            <BlueIcon
              icon={openInternalData ? 'chevron-down' : 'chevron-right'}
              color="#8F95B2"
            />
          </Chevron>
        </Head>

        {openInternalData && (
          <ItemBody>
            <LabelMenu
              label="Original Product Name"
              pinned={!!config.find(item => item.fieldName === 'importedName')}
              fieldName="importedName"
            >
              <EditableField
                parameter="importedName"
                content={
                  equipment.importedName
                    ? equipment.importedName.toString()
                    : ''
                }
              />
            </LabelMenu>
            <LabelMenu
              label="Original Manufacturer"
              pinned={
                !!config.find(
                  item => item.fieldName === 'importedManufacturerName',
                )
              }
              fieldName="importedManufacturerName"
            >
              <EditableField
                parameter="importedManufacturerName"
                content={
                  equipment.importedManufacturerName
                    ? equipment.importedManufacturerName.toString()
                    : ''
                }
              />
            </LabelMenu>
            <LabelMenu
              label="Original Model #"
              pinned={!!config.find(item => item.fieldName === 'importedModel')}
              fieldName="importedModel"
            >
              <EditableField
                parameter="importedModel"
                content={
                  equipment.importedModel
                    ? equipment.importedModel.toString()
                    : ''
                }
              />
            </LabelMenu>
            <LabelMenu
              label="Serial #"
              pinned={!!config.find(item => item.fieldName === 'serial')}
              fieldName="serial"
            >
              <EditableField parameter="serial" content={equipment.serial} />
            </LabelMenu>

            <LabelMenu
              label="Purchased From"
              pinned={!!config.find(item => item.fieldName === 'purchasedFrom')}
              fieldName="purchasedFrom"
            >
              <EditableField
                parameter="purchasedFrom"
                content={equipment.purchasedFrom}
              />
            </LabelMenu>

            <LabelMenu
              label="Purchased Price"
              pinned={
                !!config.find(item => item.fieldName === 'purchasedPrice')
              }
              fieldName="purchasedPrice"
            >
              <EditableField
                parameter="purchasedPrice"
                type="currency"
                content={
                  equipment.purchasedPrice
                    ? equipment.purchasedPrice.toString()
                    : ''
                }
              />
            </LabelMenu>

            <LabelMenu
              label="Assignee"
              pinned={!!config.find(item => item.fieldName === 'userName')}
              fieldName="userName"
            >
              <Assignee />
            </LabelMenu>

            <LabelMenu
              label="SLA"
              pinned={!!config.find(item => item.fieldName === 'sla')}
              fieldName="sla"
            >
              <EditableField parameter="sla" content={equipment.sla} />
            </LabelMenu>

            <LabelMenu
              label="Vendor"
              pinned={!!config.find(item => item.fieldName === 'vendor')}
              fieldName="vendor"
            >
              <EditableField
                parameter="vendor"
                content={equipment.vendor ? equipment.vendor : ''}
              />
            </LabelMenu>

            <LabelMenu
              label="Installed Firmware Version"
              pinned={
                !!config.find(
                  item => item.fieldName === 'installedFirmwareVersion',
                )
              }
              fieldName="installedFirmwareVersion"
            >
              <EditableField
                parameter="installedFirmwareVersion"
                content={equipment.installedFirmwareVersion}
              />
            </LabelMenu>

            <LabelMenu
              label="Installed Software Version"
              pinned={
                !!config.find(
                  item => item.fieldName === 'installedSoftwareVersion',
                )
              }
              fieldName="installedSoftwareVersion"
            >
              <EditableField
                parameter="installedSoftwareVersion"
                content={equipment.installedSoftwareVersion}
              />
            </LabelMenu>

            <LabelMenu
              label="Purchased Date"
              pinned={!!config.find(item => item.fieldName === 'purchasedDate')}
              fieldName="purchasedDate"
            >
              <EditableDateField
                parameter="purchasedDate"
                content={equipment.purchasedDate}
              />
            </LabelMenu>

            <LabelMenu
              label="Install Date"
              pinned={!!config.find(item => item.fieldName === 'installDate')}
              fieldName="installDate"
            >
              <EditableDateField
                parameter="installDate"
                content={equipment.installDate}
              />
            </LabelMenu>

            <LabelMenu
              label="License Start Date"
              pinned={
                !!config.find(item => item.fieldName === 'licenseStartDate')
              }
              fieldName="licenseStartDate"
            >
              <EditableDateField
                parameter="licenseStartDate"
                content={equipment.licenseStartDate}
              />
            </LabelMenu>

            <LabelMenu
              label="License End Date"
              pinned={
                !!config.find(item => item.fieldName === 'licenseEndDate')
              }
              fieldName="licenseEndDate"
            >
              <EditableDateField
                parameter="licenseEndDate"
                content={equipment.licenseEndDate}
              />
            </LabelMenu>
            <LabelMenu
              label="Added to Beam"
              pinned={!!config.find(item => item.fieldName === 'createdAt')}
              fieldName="createdAt"
            >
              <UnEditableField
                value={equipment.createdAt}
                fieldName="createdAt"
              />
            </LabelMenu>
            {customFields?.map(customField => {
              const fieldContent = fieldsContent.find(
                field => field.customFieldId === customField.id,
              );
              return (
                <LabelMenu
                  label={customField.name}
                  key={customField.id}
                  pinned={
                    !!config.find(
                      item => item.customField?.id === customField.id,
                    )
                  }
                  customFieldId={customField.id}
                >
                  <EditableCustomField
                    key={customField.id}
                    fieldContent={fieldContent}
                    customField={customField}
                    equipment={equipment}
                  />
                </LabelMenu>
              );
            })}
          </ItemBody>
        )}
      </Item>

      <DataSection title="Product Data">
        {Items.map(item => (
          <DataItem
            equipment={equipmentQuery.data}
            config={configQuery.data}
            item={item}
            key={item.fieldName}
          />
        ))}
        {/* <LabelMenu
          label={productName}
          pinned={!!config.find(item => item.name === productName)}
          fieldName="productName"
        >
          <FieldContent empty={!name}>{name || 'Empty'}</FieldContent>
        </LabelMenu>
        <LabelMenu
          label={productModel}
          pinned={!!config.find(item => item.name === productModel)}
          fieldName="productModel"
        >
          <FieldContent empty={!model}>{model || 'Empty'}</FieldContent>
        </LabelMenu>
        <LabelMenu
          label="Product Status"
          pinned={!!config.find(item => item.name === 'Product Status')}
          fieldName="productStatus"
        >
          <FieldContent empty={!productStatus}>
            {productStatus || 'Empty'}
          </FieldContent>
        </LabelMenu>
        <LabelMenu
          label="Product Warranty"
          pinned={!!config.find(item => item.name === 'Product Warranty')}
          fieldName="productWarranty"
        >
          <FieldContent empty={!productWarranty}>
            {productWarranty || 'Empty'}
          </FieldContent>
        </LabelMenu>
        <LabelMenu
          label={productCategory}
          pinned={!!config.find(item => item.name === productCategory)}
          fieldName="productCategory"
        >
          <FieldContent empty={!product?.category}>
            {product?.category?.name ?? 'Empty'}
          </FieldContent>
        </LabelMenu>
        <LabelMenu
          label="Page URL"
          pinned={!!config.find(item => item.name === 'Page URL')}
          fieldName="productPage"
        >
          <FieldContent
            empty={
              !product?.productPage ||
              product?.productPage?.indexOf('bhphotovideo') !== -1
            }
          >
            {product?.productPage &&
            product?.productPage?.indexOf('bhphotovideo') === -1 ? (
              <DataLink
                href={product?.productPage}
                target="_blank"
                rel="noopener noreferrer"
              >
                Link
              </DataLink>
            ) : (
              'Empty'
            )}
          </FieldContent>
        </LabelMenu>
        <LabelMenu
          label={manufacturerFieldName}
          pinned={!!config.find(item => item.name === manufacturerFieldName)}
          fieldName="manufacturerName"
        >
          <FieldContent empty={!manufacturerName}>
            {manufacturerName || 'Empty'}
          </FieldContent>
        </LabelMenu>
        <LabelMenu
          label="Description"
          pinned={!!config.find(item => item.name === productDescription)}
          fieldName="productDescription"
        >
          <FieldContent empty={!product?.description}>
            {product?.description || 'Empty'}
          </FieldContent>
        </LabelMenu> */}
      </DataSection>
      <DataSection title="Product Specs">
        <ProductSpecsBody productId={productId} />
      </DataSection>
    </div>
  );
};
